import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { CONTACT_AREAS } from "@/lib/cta";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const INTEREST_TO_AREA = {
  "sensitive-case": "Sensitive Case Advisory",
  "investigation": "Workplace Investigation",
  "people-risk": "People Risk Advisory",
  "er-diagnostic": "ER Capability Diagnostic",
  "er-framework": "Scalable ER Function Build",
  "fractional": "Fractional ER & People Risk Partner",
  "general": "General Enquiry",
};

const empty = { name: "", company: "", email: "", area_of_interest: "", message: "" };

export function Contact() {
  const [params] = useSearchParams();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const area = INTEREST_TO_AREA[params.get("interest")];
    if (area) setForm((f) => ({ ...f, area_of_interest: area }));
  }, [params]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.area_of_interest || !form.message) {
      toast.error("Please complete the required fields.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      if (window.gtag) window.gtag("event", "contact_enquiry", { area: form.area_of_interest });
      toast.success(data.message || "Enquiry received in strict confidence.");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const field = "bg-transparent border-0 border-b border-white/20 rounded-none px-0 h-12 text-bone placeholder:text-muted2/60 focus-visible:ring-0 focus-visible:border-gold text-base";

  return (
    <section id="contact" data-testid="contact-section" className="py-28 px-6 md:px-12 bg-ink border-t border-white/10">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-20">
        <div className="md:col-span-5">
          <Reveal>
            <p className="font-mono text-[11px] tracking-[0.35em] text-gold uppercase mb-6">Prefer to write first?</p>
            <h2 className="font-serif font-light text-4xl md:text-6xl tracking-tight mb-8">
              Send a confidential enquiry.
            </h2>
            <p className="text-muted2 leading-relaxed mb-6 max-w-md">
              Ready to talk now? <strong className="text-bone">Book a Consultation</strong> for a complimentary
              introductory call. Prefer to write? Share a little below and I'll respond personally, in complete
              discretion.
            </p>
            <p className="text-muted2/80 text-sm leading-relaxed mb-10 max-w-md">
              Please don't include confidential employee names or detailed case evidence — we can discuss sensitive
              detail privately.
            </p>
            <div className="space-y-4 font-mono text-sm">
              <a href="mailto:irina@ishikadvisory.com" data-testid="contact-email-link" className="block text-bone hover:text-gold transition-colors">irina@ishikadvisory.com</a>
              <a href="mailto:irinaishik@gmail.com" data-testid="contact-email-link-alt" className="block text-bone hover:text-gold transition-colors">irinaishik@gmail.com</a>
              <a href="tel:+971507736237" data-testid="contact-phone-link" className="block text-bone hover:text-gold transition-colors">+971 50 773 62 37</a>
              <p className="text-muted2 tracking-[0.15em] uppercase text-[11px]">Dubai · UAE</p>
            </div>
          </Reveal>
        </div>

        <div className="md:col-span-7">
          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <Input data-testid="contact-name" className={field} placeholder="Full name *" value={form.name} onChange={(e) => set("name", e.target.value)} />
                <Input data-testid="contact-company" className={field} placeholder="Company / Organisation" value={form.company} onChange={(e) => set("company", e.target.value)} />
              </div>
              <Input data-testid="contact-email" type="email" className={field} placeholder="Business email *" value={form.email} onChange={(e) => set("email", e.target.value)} />

              <Select value={form.area_of_interest} onValueChange={(v) => set("area_of_interest", v)}>
                <SelectTrigger data-testid="contact-area" className={field + " w-full justify-between"}>
                  <SelectValue placeholder="Area of interest *" />
                </SelectTrigger>
                <SelectContent className="bg-elevated border-white/15 text-bone">
                  {CONTACT_AREAS.map((a) => (
                    <SelectItem key={a} value={a} data-testid={`area-option-${a}`} className="focus:bg-gold focus:text-ink">{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Textarea data-testid="contact-message" className={field + " min-h-[120px] py-3"} placeholder="How can I help? (no confidential case detail, please) *" value={form.message} onChange={(e) => set("message", e.target.value)} />

              <button type="submit" disabled={loading} data-testid="contact-submit"
                className="font-sans text-xs uppercase tracking-[0.2em] bg-gold text-ink px-10 py-4 hover:bg-goldhover transition-colors duration-300 disabled:opacity-50">
                {loading ? "Sending…" : "Send Enquiry"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
